import { computeAnnualVolumeM3 } from '../../../../utils/consumptionCalculations.js';
import { computeHotWaterPrice, getEffectiveHotWaterRatio } from './waterPricing.js';
import { computePaybackYears } from './profitability.js';

// Placeholder default: users partly compensate a reduced flow by running the tap
// longer, eating into the theoretical savings. Trivially tunable — single source of truth.
export const DEFAULT_REBOUND_RATE = 0.15;

export const getUsagesForEquipment = (equipmentId, usages) =>
  usages.filter(u => u._id_equipement === equipmentId);

/**
 * Simulates the effect of replacing an equipment's flow rate with `targetFlow`.
 * Works for any equipment type whose consumption model is driven by `water_value`
 * (faucets, showers) via the shared computeAnnualVolumeM3.
 * Usages with a known fixed volume (is_known_volume) are not affected by flow rate
 * changes: they're counted in the current baseline but contribute zero savings.
 * `reboundRate` discounts the theoretical flow-based savings (rebound effect).
 *
 * Volumes are tracked separately for hot- and cold-water usages, split proportionally
 * by each usage's effective hot-water ratio (see getEffectiveHotWaterRatio) — a usage
 * mixed 30% hot / 70% cold contributes 30% of its volume to the hot accumulator and
 * 70% to the cold one, rather than being routed wholesale into a single bucket.
 * The blended fields (currentM3/newM3/savingsM3) remain the sum of both.
 */
export const simulateEquipmentSavings = (equipment, usages, targetFlow, reboundRate = 0) => {
  const equipmentUsages = getUsagesForEquipment(equipment._id, usages);

  let currentM3Cold = 0, currentM3Hot = 0;
  let rawNewM3Cold = 0, rawNewM3Hot = 0;
  let hasNonReducibleUsage = false;

  equipmentUsages.forEach(usage => {
    const ratio = getEffectiveHotWaterRatio({ usage, equipment });
    if (usage.is_known_volume) {
      hasNonReducibleUsage = true;
      const fixedM3 = usage.annual_volume || 0;
      currentM3Hot += fixedM3 * ratio;
      currentM3Cold += fixedM3 * (1 - ratio);
      rawNewM3Hot += fixedM3 * ratio;
      rawNewM3Cold += fixedM3 * (1 - ratio);
      return;
    }
    const current = computeAnnualVolumeM3(equipment, usage);
    const rawNew = computeAnnualVolumeM3({ ...equipment, water_value: targetFlow }, usage);
    currentM3Hot += current * ratio;
    currentM3Cold += current * (1 - ratio);
    rawNewM3Hot += rawNew * ratio;
    rawNewM3Cold += rawNew * (1 - ratio);
  });

  const savingsM3Cold = Math.max(0, currentM3Cold - rawNewM3Cold) * (1 - reboundRate);
  const savingsM3Hot = Math.max(0, currentM3Hot - rawNewM3Hot) * (1 - reboundRate);
  const newM3Cold = currentM3Cold - savingsM3Cold;
  const newM3Hot = currentM3Hot - savingsM3Hot;

  const currentM3 = currentM3Cold + currentM3Hot;
  const newM3 = newM3Cold + newM3Hot;
  const savingsM3 = savingsM3Cold + savingsM3Hot;
  const savingsPct = currentM3 > 0 ? (savingsM3 / currentM3) * 100 : 0;

  return {
    equipmentId: equipment._id,
    currentM3,
    newM3,
    savingsM3,
    savingsPct,
    currentM3Cold,
    currentM3Hot,
    newM3Cold,
    newM3Hot,
    savingsM3Cold,
    savingsM3Hot,
    hasNonReducibleUsage,
    currentFlow: equipment.water_value,
    targetFlow,
  };
};

/**
 * @param {Object[]} equipments - selected equipments to simulate (already filtered to one equipment type)
 * @param {Object[]} usages - all usages for these equipments
 * @param {number|null} [targetFlow] - global target flow rate (L/min), used as a fallback for any
 *   equipment not covered by `overrides`
 * @param {Object|null} waterPricing - { waterPrice, electricityPrice, fuelPrice, waterHeatingType, hotWaterSetpoint, waterHeatingEfficiency }
 * @param {Map<string, number>} [overrides] - per-equipment target flow (e.g. one per product lot),
 *   takes precedence over `targetFlow` for the equipment it covers
 * @param {Map<string, number|null>} [priceOverrides] - per-equipment unit price (from the lot's
 *   catalog model), null for equipment assigned a manual flow with no catalog price
 */
export const simulateFlowReductionSavings = ({ equipments, usages, targetFlow = null, waterPricing, overrides = null, priceOverrides = null, reboundRate = 0 }) => {
  if (!equipments || equipments.length === 0) {
    return { ready: false };
  }

  const resolveTargetFlow = (equipment) => overrides?.get(equipment._id) ?? targetFlow;
  const allResolved = equipments.every(e => {
    const flow = resolveTargetFlow(e);
    return flow != null && flow > 0;
  });
  if (!allResolved) {
    return { ready: false };
  }

  const perEquipment = equipments.map(equipment =>
    simulateEquipmentSavings(equipment, usages, resolveTargetFlow(equipment), reboundRate)
  );

  const totalCurrentM3 = perEquipment.reduce((sum, r) => sum + r.currentM3, 0);
  const totalNewM3 = perEquipment.reduce((sum, r) => sum + r.newM3, 0);
  const totalSavingsM3 = totalCurrentM3 - totalNewM3;
  const savingsPct = totalCurrentM3 > 0 ? (totalSavingsM3 / totalCurrentM3) * 100 : 0;

  const totalSavingsM3Cold = perEquipment.reduce((sum, r) => sum + r.savingsM3Cold, 0);
  const totalSavingsM3Hot = perEquipment.reduce((sum, r) => sum + r.savingsM3Hot, 0);

  const coldWaterPrice = waterPricing?.waterPrice ?? null;
  const hotWaterPrice = computeHotWaterPrice({
    waterPrice: coldWaterPrice,
    electricityPrice: waterPricing?.electricityPrice ?? null,
    fuelPrice: waterPricing?.fuelPrice ?? null,
    waterHeatingType: waterPricing?.waterHeatingType ?? null,
    hotWaterSetpoint: waterPricing?.hotWaterSetpoint ?? null,
    waterHeatingEfficiency: waterPricing?.waterHeatingEfficiency ?? null,
  });

  const waterPriceAvailable = coldWaterPrice != null && coldWaterPrice > 0;
  const savingsEurWaterPerYear = waterPriceAvailable ? totalSavingsM3 * coldWaterPrice : null;

  let heatingPriceAvailable;
  let savingsEurHeatingPerYear;
  if (totalSavingsM3Hot <= 0) {
    heatingPriceAvailable = true;
    savingsEurHeatingPerYear = 0;
  } else if (hotWaterPrice != null && coldWaterPrice != null) {
    heatingPriceAvailable = true;
    savingsEurHeatingPerYear = totalSavingsM3Hot * (hotWaterPrice - coldWaterPrice);
  } else {
    heatingPriceAvailable = false;
    savingsEurHeatingPerYear = null;
  }

  const savingsEurTotalPerYear = waterPriceAvailable
    ? savingsEurWaterPerYear + (heatingPriceAvailable ? savingsEurHeatingPerYear : 0)
    : null;

  // Un document équipement peut représenter plusieurs points d'eau physiques :
  // technical_data.nb_equipment (plusieurs équipements identiques dans une pièce)
  // et location.room_multiplier (pièce répétée à l'identique, cf. nb_identical_rooms).
  const investmentFullyKnown = equipments.every(e => (priceOverrides?.get(e._id) ?? null) != null);
  const totalInvestmentEur = investmentFullyKnown
    ? equipments.reduce((sum, e) => {
        const nbEquipment = e.technical_data?.nb_equipment ?? 1;
        const roomMultiplier = e.location?.room_multiplier ?? 1;
        return sum + (priceOverrides?.get(e._id) ?? 0) * nbEquipment * roomMultiplier;
      }, 0)
    : null;
  const paybackYears = computePaybackYears({
    investmentEur: totalInvestmentEur,
    investmentFullyKnown,
    savingsEurPerYear: savingsEurTotalPerYear,
  });

  return {
    ready: true,
    perEquipment,
    totalCurrentM3,
    totalNewM3,
    totalSavingsM3,
    totalSavingsM3Cold,
    totalSavingsM3Hot,
    savingsPct,
    waterPriceAvailable,
    heatingPriceAvailable,
    savingsEurWaterPerYear,
    savingsEurHeatingPerYear,
    savingsEurTotalPerYear,
    totalInvestmentEur,
    investmentFullyKnown,
    paybackYears,
  };
};
